import { action, computed, observable, reaction, makeObservable } from 'mobx';
import React from 'react';
import {Disposers} from '../../../helpers';
import {DraggableId, DroppableChildProvided, DroppableId} from '../models';
import {DragDropContextStore} from '../drag-drop-context/DragDropContextStore';
import {DroppableImpl} from './DroppableImpl';
import {DraggableStore} from '../draggable/DraggableStore';

let instanceIdCounter = 0;

export class DroppableStore {
  readonly instanceId: number;
  readonly droppableId: DroppableId;

  readonly contextStore: DragDropContextStore;
  readonly parent: DroppableStore | null;

  readonly droppableRef: React.RefObject<any>;

  @observable
  draggingFromThisWith: DraggableId | null;
  @observable
  draggingOverWith: DraggableId | null;

  @observable.shallow
  readonly droppableStores: DroppableStore[];

  @observable.shallow
  readonly draggableStores: DraggableStore[];

  @observable
  disposed: boolean;

  private readonly disposers = new Disposers();

  constructor(readonly component: DroppableImpl) {
    makeObservable(this);
    this.instanceId = instanceIdCounter++;
    this.droppableId = this.component.props.droppableId;
    this.droppableRef = React.createRef();

    this.contextStore = component.props.contextStore;
    this.parent = component.props.parent;
    this.draggableStores = [];
    this.droppableStores = [];

    this.disposed = false;

    this.draggingFromThisWith = null;
    this.draggingOverWith = null;

    this.setup();
  }

  private setup() {
    this.disposers.add(reaction(
      () => this.droppableStores.map(x => x.disposed),
      () => {
        for (let droppableStore of this.droppableStores)
          if (droppableStore.disposed)
            this.removeDroppableStore(droppableStore);
      }
    ));
    this.disposers.add(reaction(
      () => this.draggableStores.map(x => x.disposed),
      () => {
        for (let draggableStore of this.draggableStores)
          if (draggableStore.disposed)
            this.removeDraggableStore(draggableStore);
      }
    ));
  }

  @action
  mount() {
    if (this.parent !== null)
      this.parent.addDroppableStore(this);
    else
      this.contextStore.addDroppableStore(this);
  }

  @action
  addDroppableStore = (store: DroppableStore) => {
    let index = this.droppableStores.findIndex((x) => x.instanceId === store.instanceId);
    if (index !== -1)
      return;
    this.droppableStores.push(store);
  };

  @action
  removeDroppableStore(store: DroppableStore): void {
    let index = this.droppableStores.findIndex((x) => x.instanceId === store.instanceId);
    if (index === -1)
      return;
    this.droppableStores.splice(index, 1);
  }

  @action
  addDraggableStore = (store: DraggableStore) => {
    let index = this.draggableStores.findIndex((x) => x.instanceId === store.instanceId);
    if (index !== -1)
      return;
    this.draggableStores.push(store);
  };

  @action
  removeDraggableStore(store: DraggableStore): void {
    let index = this.draggableStores.findIndex((x) => x.instanceId === store.instanceId);
    if (index === -1)
      return;
    this.draggableStores.splice(index, 1);
  }

  @action
  setDraggingOverWith(draggableId: DraggableId | null) {
    if (draggableId === this.draggingOverWith)
      return;
    this.draggingOverWith = draggableId;
  }

  @action
  setDraggingFromThisWith(draggableId: DraggableId | null) {
    if (this.draggingFromThisWith === draggableId)
      return;
    this.draggingFromThisWith = draggableId;
  }

  @computed
  get droppableChildProvided(): DroppableChildProvided {
    return {
      droppableId: this.droppableId,
      droppableProps: {ref: this.droppableRef},
      isDraggingOver: this.draggingOverWith !== null,
      draggingOverWith: this.draggingOverWith,
      draggingFromThisWith: this.draggingFromThisWith
    };
  }

  @action
  dispose() {
    if (this.parent !== null)
      this.parent.removeDroppableStore(this);
    else
      this.contextStore.removeDroppableStore(this);

    this.disposers.dispose();
    this.disposed = true;
  }
}
